import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { closeAlert, errorActions, uploadData } from "@/alerts";
import { getCompany } from "@/publicjs/company";
import { getCurrentDate } from "@/publicjs/currentDate";
import { formatDateTime } from "@/publicjs/formatDate";
import { idLocalStore } from "@/publicjs/localStorage";
import { imageInBase64 } from "@/publicjs/logoCompany";
import { format } from "@/publicjs/money";
import styles from "@/publicjs/styles";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export const exportToPDF = async (registers, summary, filters) => {
  await uploadData(
    "Generando reporte",
    "Espera un momento mientras preparamos el PDF de actividad de usuarios...",
  );

  const company = await getCompany(await idLocalStore());
  const base64Image = await imageInBase64(company.companyLogo);

  const activeFilters = [
    filters?.dateStart ? `Desde ${filters.dateStart}` : null,
    filters?.dateEnd ? `Hasta ${filters.dateEnd}` : null,
    filters?.module ? `Módulo: ${filters.module}` : null,
    filters?.statusScope && filters.statusScope !== "ALL"
      ? `Resultado: ${
          filters.statusScope === "ERROR" ? "Con errores" : "Exitoso"
        }`
      : null,
    filters?.search ? `Búsqueda: ${filters.search}` : null,
  ].filter(Boolean);

  try {
    const docDefinition = {
      pageSize: "LEGAL",
      pageOrientation: "landscape",
      pageMargins: [18, 132, 18, 20],
      header(currentPage, pageCount, pageSize) {
        return [
          {
            columns: [
              {
                width: 100,
                stack: [{ image: base64Image, fit: [80, 80] }],
                alignment: "left",
                margin: [0, 10, 0, 0],
              },
              {
                width: 240,
                stack: [
                  {
                    text: company.companyName,
                    fontSize: styles.fontSize.pdf.xl,
                    alignment: "left",
                    bold: true,
                    color: styles.colors.colorTitle,
                  },
                  {
                    text: "Dirección: " + company.companyAddress,
                    fontSize: styles.fontSize.pdf.sm,
                    alignment: "left",
                    color: styles.colors.colorDescription,
                  },
                  {
                    text: "Tel: " + company.companycellphone,
                    fontSize: styles.fontSize.pdf.sm,
                    alignment: "left",
                    color: styles.colors.colorDescription,
                  },
                ],
                margin: [12, 10, 24, 0],
              },
              {
                width: "*",
                stack: [
                  {
                    text: company.storeName,
                    fontSize: styles.fontSize.pdf.lg,
                    alignment: "right",
                    color: styles.colors.colorTitle,
                    bold: true,
                  },
                  {
                    text: "Reporte de actividad de usuarios",
                    fontSize: styles.fontSize.pdf.sm,
                    alignment: "right",
                    color: styles.colors.colorBlue,
                    bold: true,
                  },
                  {
                    text: getCurrentDate(),
                    fontSize: styles.fontSize.pdf.sm,
                    alignment: "right",
                    color: styles.colors.colorDescription,
                    bold: true,
                  },
                ],
                margin: [0, 10, 24, 0],
              },
            ],
            margin: [24, 12, 0, 0],
          },
          {
            canvas: [
              { type: "rect", x: 18, y: 90, w: pageSize.width - 36, h: 2 },
            ],
            margin: [0, 10],
          },
        ];
      },
      footer(currentPage, pageCount) {
        return {
          text: `Página ${currentPage} de ${pageCount}`,
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorDescription,
          margin: [0, 0, 24, 0],
        };
      },
      content: [
        {
          text: `Eventos: ${format(
            summary?.total_events || 0,
          )} | Errores: ${format(
            summary?.total_errors || 0,
          )} | Usuarios activos: ${format(
            summary?.active_users || 0,
          )} | Pasos únicos: ${format(summary?.unique_steps || 0)}`,
          margin: [0, 0, 0, 8],
          color: styles.colors.colorTitle,
          bold: true,
        },
        {
          text: activeFilters.length
            ? activeFilters.join(" | ")
            : "Sin filtros adicionales",
          margin: [0, 0, 0, 16],
          color: styles.colors.colorDescription,
        },
        {
          table: {
            headerRows: 1,
            dontBreakRows: true,
            widths: [80, 150, 95, 170, "*", 55, 95],
            body: [
              [
                {
                  text: "Usuario",
                  bold: true,
                  border: [false, false, false, true],
                },
                {
                  text: "Pantalla",
                  bold: true,
                  border: [false, false, false, true],
                },
                {
                  text: "Módulo",
                  bold: true,
                  border: [false, false, false, true],
                },
                {
                  text: "Ruta",
                  bold: true,
                  border: [false, false, false, true],
                },
                {
                  text: "Acción",
                  bold: true,
                  border: [false, false, false, true],
                },
                {
                  text: "Errores",
                  bold: true,
                  border: [false, false, false, true],
                  alignment: "right",
                },
                {
                  text: "Última actividad",
                  bold: true,
                  border: [false, false, false, true],
                },
              ],
              ...registers.map((register) => [
                {
                  text: register.usuario || "---",
                  border: [false, false, false, false],
                  fontSize: styles.fontSize.table.sm,
                },
                {
                  text: register.pantalla || "---",
                  border: [false, false, false, false],
                  fontSize: styles.fontSize.table.sm,
                },
                {
                  text: register.modulo || "---",
                  border: [false, false, false, false],
                  fontSize: styles.fontSize.table.sm,
                },
                {
                  text: register.ruta_cliente || "---",
                  border: [false, false, false, false],
                  fontSize: styles.fontSize.table.sm,
                },
                {
                  text: register.accion || "---",
                  border: [false, false, false, false],
                  fontSize: styles.fontSize.table.sm,
                },
                {
                  text: format(register.total_errores || 0),
                  border: [false, false, false, false],
                  fontSize: styles.fontSize.table.sm,
                  alignment: "right",
                },
                {
                  text: register.ultima_actividad_at
                    ? formatDateTime(register.ultima_actividad_at)
                    : "---",
                  border: [false, false, false, false],
                  fontSize: styles.fontSize.table.sm,
                },
              ]),
            ],
          },
        },
      ],
    };

    pdfMake.createPdf(docDefinition).open();
    await closeAlert();
  } catch (error) {
    errorActions("No pudimos generar el PDF de actividad de usuarios");
  }
};
